import React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../assets";

export const whatsappUrl = "https://wa.me/5548996087396";

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || "pt").split("-")[0];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("i18nextLng", lng);
    } catch {}
  };

  return (
    <div className="language-switcher" role="group" aria-label="Seletor de idioma">
      <button
        type="button"
        className={current === "pt" ? "active" : ""}
        onClick={() => changeLanguage("pt")}
        aria-label="Português"
      >
        PT
      </button>
      <span className="lang-divider">/</span>
      <button
        type="button"
        className={current === "en" ? "active" : ""}
        onClick={() => changeLanguage("en")}
        aria-label="English"
      >
        EN
      </button>
      <span className="lang-divider">/</span>
      <button
        type="button"
        className={current === "es" ? "active" : ""}
        onClick={() => changeLanguage("es")}
        aria-label="Español"
      >
        ES
      </button>
    </div>
  );
}

export function SiteHeader() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  return (
    <header className="site-header">
      <Link to="/" className="brand" aria-label="Executive Jiu-Jitsu — página inicial" onClick={closeMenu}>
        <img className="brand-logo" src={IMAGES.logo} alt="Logo Executive Jiu-Jitsu" />
        <span>
          EXECUTIVE <strong>JIU-JITSU</strong>
          <small>PROFESSOR VAGNER LIMA</small>
        </span>
      </Link>

      {/* Desktop Navigation */}
      <nav className="desktop-nav" aria-label="Navegação do site">
        <Link to="/" activeOptions={{ exact: true }} activeProps={{ className: "active" }}>
          {t("nav.home")}
        </Link>
        <Link to="/adult" activeProps={{ className: "active" }}>
          {t("nav.adult")}
        </Link>
        <Link to="/kids" activeProps={{ className: "active" }}>
          {t("nav.kids")}
        </Link>
        <Link to="/radar" activeProps={{ className: "active" }}>
          {t("nav.radar")}
        </Link>
        <Link to="/partners" activeProps={{ className: "active" }}>
          {t("nav.partners")}
        </Link>
      </nav>

      <div className="header-right">
        <div className="desktop-lang-switcher">
          <LanguageSwitcher />
        </div>
        <a className="header-cta" href={whatsappUrl} target="_blank" rel="noopener noreferrer">
          {t("nav.scheduleTrial")}
        </a>
        <button
          type="button"
          className={`mobile-menu-btn ${isOpen ? "open" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={isOpen}
        >
          <span className="hamburger-bar"></span>
          <span className="hamburger-bar"></span>
          <span className="hamburger-bar"></span>
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      <div
        className={`mobile-nav-backdrop ${isOpen ? "open" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Mobile Drawer Panel */}
      <div className={`mobile-nav-drawer ${isOpen ? "open" : ""}`} aria-label="Menu de Navegação Mobile">
        <div className="mobile-nav-header">
          <Link to="/" className="brand" onClick={closeMenu}>
            <img className="brand-logo" src={IMAGES.logo} alt="Logo Executive Jiu-Jitsu" />
            <span>
              EXECUTIVE <strong>JIU-JITSU</strong>
              <small>PROFESSOR VAGNER LIMA</small>
            </span>
          </Link>
          <button
            type="button"
            className="mobile-close-btn"
            onClick={closeMenu}
            aria-label="Fechar menu"
          >
            ✕
          </button>
        </div>

        <nav className="mobile-nav-links" aria-label="Navegação mobile">
          <Link to="/" activeOptions={{ exact: true }} activeProps={{ className: "active" }} onClick={closeMenu}>
            {t("nav.home")}
          </Link>
          <Link to="/adult" activeProps={{ className: "active" }} onClick={closeMenu}>
            {t("nav.adult")}
          </Link>
          <Link to="/kids" activeProps={{ className: "active" }} onClick={closeMenu}>
            {t("nav.kids")}
          </Link>
          <Link to="/radar" activeProps={{ className: "active" }} onClick={closeMenu}>
            {t("nav.radar")}
          </Link>
          <Link to="/partners" activeProps={{ className: "active" }} onClick={closeMenu}>
            {t("nav.partners")}
          </Link>
        </nav>

        <div className="mobile-nav-footer">
          <div className="mobile-lang-container">
            <span className="mobile-lang-label">Idioma / Language</span>
            <LanguageSwitcher />
          </div>
          <a
            className="button mobile-cta-btn"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            {t("nav.scheduleTrial")}
          </a>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const { t } = useTranslation();

  return (
    <footer className="site-footer">
      <Link to="/" className="footer-brand">
        <img src={IMAGES.logo} alt="Logo Executive Jiu-Jitsu" />
        <span>
          EXECUTIVE
          <br />
          <strong>JIU-JITSU</strong>
        </span>
      </Link>
      <div className="footer-center">
        <p className="footer-tagline">{t("footer.tagline")}</p>
        <div className="footer-legal-links">
          <Link to="/privacy">{t("footer.privacy")}</Link>
          <span className="footer-divider">•</span>
          <Link to="/terms">{t("footer.terms")}</Link>
          <span className="footer-divider">•</span>
          <Link to="/data-deletion">{t("footer.dataDeletion")}</Link>
        </div>
      </div>
      <div className="footer-contact">
        <a href="https://instagram.com/vagnao_bjjlima" target="_blank" rel="noopener noreferrer">
          @vagnao_bjjlima
        </a>
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
          (48) 99608-7396
        </a>
        <small>{t("footer.copyright")}</small>
      </div>
    </footer>
  );
}

export function ContactBanner({ kind }: { kind: "adult" | "kids" }) {
  const { t } = useTranslation();
  const isKids = kind === "kids";
  const prefix = isKids ? "contactBanner.kids" : "contactBanner.adult";

  return (
    <section className="contact-banner">
      <p className="eyebrow">{t(`${prefix}.eyebrow`)}</p>
      <h2>{t(`${prefix}.title`)}</h2>
      <p>{t(`${prefix}.description`)}</p>
      <a
        className="button dark-button"
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t(`${prefix}.cta`)}
      </a>
    </section>
  );
}
